import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ElementRef
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { environment } from "../../../../../environments/environment";
import { MetaTagService } from "../../../services/meta-tag.service";
import { NxtPodcastService } from "../../../services/nxt-podcast.service";
import { NxtPodcastEpisode } from "../../../models/nxt-podcast-episode.model";

@Component({
  selector: "app-nxt-podcast",
  templateUrl: "./nxt-podcast.component.html",
  styleUrls: ["./nxt-podcast.component.scss"]
})
export class NxtPodcastComponent implements OnInit, OnDestroy {
  readonly playlistId: string = "PLnEjq-A0vrMGwsByAyTn-fpEIJH13tWpM";
  readonly playlistUrl: string = `https://www.youtube.com/playlist?list=${this.playlistId}`;

  episodes: NxtPodcastEpisode[] = [];

  activeVideoId: string = null;
  safeEmbedUrl: SafeResourceUrl = null;
  isAtStart = true;
  isAtEnd = false;
  showBackToTop = false;

  private lastFocused: HTMLElement = null;

  @ViewChild("track") trackRef: ElementRef<HTMLElement>;
  @ViewChild("modalClose") modalCloseRef: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private metaTagSrv: MetaTagService,
    private sanitizer: DomSanitizer,
    private nxtPodcastSrv: NxtPodcastService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(() => {
      this.metaTagSrv.setNxtPodcastMetaTags();
    });
    this.nxtPodcastSrv.getEpisodes().subscribe({
      next: episodes => (this.episodes = episodes),
      error: () => (this.episodes = [])
    });
  }

  ngOnDestroy(): void {
    document.body.style.overflow = "";
  }

  thumbUrl(episode: NxtPodcastEpisode): string {
    return episode.videoId
      ? `https://i.ytimg.com/vi/${episode.videoId}/maxresdefault.jpg`
      : "/assets/img/nxt-podcast/episode-placeholder.jpg";
  }

  episodeAriaLabel(episode: NxtPodcastEpisode): string {
    return episode.guest
      ? `${episode.title} — ${episode.guest}, ${episode.duration}`
      : `${episode.title}, ${episode.duration}`;
  }

  openEpisode(episode: NxtPodcastEpisode, trigger: HTMLElement): void {
    if (!episode.videoId) {
      window.open(this.playlistUrl, "_blank", "noopener");
      return;
    }
    this.lastFocused = trigger;
    this.activeVideoId = episode.videoId;
    const embedUrl = `https://www.youtube.com/embed/${episode.videoId}?autoplay=1&rel=0&modestbranding=1`;
    this.safeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    document.body.style.overflow = "hidden";
    setTimeout(() => {
      if (this.modalCloseRef) {
        this.modalCloseRef.nativeElement.focus();
      }
    });
  }

  scrollToAbout(event: Event): void {
    event.preventDefault();
    const about = document.getElementById("nxt-about");
    if (about) {
      about.scrollIntoView({ behavior: "smooth" });
    }
  }

  get activeWatchUrl(): string {
    return this.activeVideoId
      ? `https://www.youtube.com/watch?v=${this.activeVideoId}`
      : this.playlistUrl;
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  closeModal(): void {
    if (!this.activeVideoId) {
      return;
    }
    this.activeVideoId = null;
    this.safeEmbedUrl = null;
    document.body.style.overflow = "";
    if (this.lastFocused) {
      this.lastFocused.focus();
    }
  }

  @HostListener("document:keydown.escape")
  onEscape(): void {
    this.closeModal();
  }

  @HostListener("window:scroll")
  onWindowScroll(): void {
    this.showBackToTop = window.scrollY > 600;
  }

  scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  private cardStep(): number {
    const track = this.trackRef.nativeElement;
    const card = track.querySelector(".nxt-card") as HTMLElement;
    return card ? card.getBoundingClientRect().width + 24 : track.clientWidth;
  }

  scrollPrev(): void {
    this.trackRef.nativeElement.scrollBy({
      left: -this.cardStep(),
      behavior: "smooth"
    });
  }

  scrollNext(): void {
    this.trackRef.nativeElement.scrollBy({
      left: this.cardStep(),
      behavior: "smooth"
    });
  }

  onTrackScroll(): void {
    const track = this.trackRef.nativeElement;
    const max = track.scrollWidth - track.clientWidth - 2;
    this.isAtStart = track.scrollLeft <= 2;
    this.isAtEnd = track.scrollLeft >= max;
  }
}
